import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
    CheckCircle2, 
    Loader2, 
    XCircle, 
    Home,
    Calendar,
    Mail
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const [status, setStatus] = useState('loading'); // loading, success, failed, expired
    const [paymentDetails, setPaymentDetails] = useState(null);
    const { clearCart } = useCart();

    useEffect(() => {
        if (sessionId) {
            pollPaymentStatus(sessionId);
        } else {
            setStatus('failed');
        }
    }, [sessionId]);

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            setStatus('failed');
            return;
        }

        try {
            const response = await axios.get(`${API}/checkout/status/${sessionId}`);
            const data = response.data;

            if (data.payment_status === 'paid') {
                setStatus('success');
                setPaymentDetails(data);
                // Clear the cart after successful payment
                clearCart();
                return;
            } else if (data.status === 'expired') {
                setStatus('expired');
                return;
            }

            // Continue polling if payment is still pending
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            if (attempts < maxAttempts - 1) {
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
            } else {
                setStatus('failed');
            }
        }
    };

    return (
        <div className="min-h-screen bg-secondary/20 flex items-center justify-center py-20" data-testid="checkout-success-page">
            <SEOHead 
                title="Booking Confirmation | Craven Canines"
                description="Your mantrailing booking confirmation."
                noindex={true}
            />
            <div className="container-custom max-w-lg">
                <Card className="shadow-xl border-0">
                    <CardContent className="p-12 text-center">
                        {status === 'loading' && (
                            <div data-testid="payment-loading">
                                <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
                                <h2 className="text-2xl font-bold text-foreground mb-4 font-serif">
                                    Processing Payment...
                                </h2>
                                <p className="text-muted-foreground">
                                    Please wait while we confirm your payment.
                                </p>
                            </div>
                        )}

                        {status === 'success' && (
                            <div data-testid="payment-success">
                                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <CheckCircle2 className="w-12 h-12 text-primary" />
                                </div>
                                <h2 className="text-2xl font-bold text-foreground mb-4 font-serif">
                                    Booking Confirmed!
                                </h2>
                                <p className="text-muted-foreground mb-6">
                                    Thank you for your booking. We're excited to see you and your dog!
                                </p>
                                
                                {paymentDetails && (
                                    <div className="bg-secondary/50 rounded-xl p-6 mb-8 text-left">
                                        <h3 className="font-semibold mb-3">Payment Details</h3>
                                        <div className="space-y-2 text-sm text-muted-foreground">
                                            <p>
                                                <strong>Amount:</strong> £{(paymentDetails.amount_total / 100).toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Status:</strong>{' '}
                                                <span className="text-primary font-medium">Paid</span>
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-3">
                                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                        <Mail className="w-4 h-4" />
                                        <span>A confirmation has been recorded</span>
                                    </div>
                                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                        <Calendar className="w-4 h-4" />
                                        <span>Check your session details in your confirmation</span>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3 mt-8">
                                    <Link to="/">
                                        <Button className="w-full btn-primary">
                                            <Home className="w-4 h-4 mr-2" />
                                            Back to Home
                                        </Button>
                                    </Link>
                                    <Link to="/mantrailing">
                                        <Button variant="outline" className="w-full rounded-full">
                                            Book Another Session
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        )}

                        {status === 'failed' && (
                            <div data-testid="payment-failed">
                                <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <XCircle className="w-12 h-12 text-destructive" />
                                </div>
                                <h2 className="text-2xl font-bold text-foreground mb-4 font-serif">
                                    Payment Issue
                                </h2>
                                <p className="text-muted-foreground mb-8">
                                    We couldn't confirm your payment. Please try again or contact us for help.
                                </p>
                                <div className="flex flex-col gap-3">
                                    <Link to="/cart">
                                        <Button className="w-full btn-primary">
                                            Return to Cart
                                        </Button>
                                    </Link>
                                    <Link to="/contact">
                                        <Button variant="outline" className="w-full rounded-full">
                                            Contact Support
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        )}

                        {status === 'expired' && (
                            <div data-testid="payment-expired">
                                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                                    <XCircle className="w-12 h-12 text-muted-foreground" />
                                </div>
                                <h2 className="text-2xl font-bold text-foreground mb-4 font-serif">
                                    Session Expired
                                </h2>
                                <p className="text-muted-foreground mb-8">
                                    Your payment session has expired. Please try booking again.
                                </p>
                                <Link to="/mantrailing">
                                    <Button className="w-full btn-primary">
                                        Book Again
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CheckoutSuccessPage;
